import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingUp, Shield, Users, BarChart3, Zap, Target, Check, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { SiLinkedin } from "react-icons/si";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const featuresFaqs = [
  { q: "What software helps manage SaaS subscriptions?", a: "SaaSGama helps businesses manage all SaaS subscriptions from a centralized dashboard, providing visibility into software usage, renewals, licenses, and spending." },
  { q: "How can companies control SaaS spending?", a: "SaaSGama helps control SaaS spending by identifying unused licenses, tracking subscription costs, monitoring renewals, and optimizing software investments across teams." },
  { q: "Which platform tracks software licenses and renewals?", a: "SaaSGama tracks software licenses, subscription renewals, contract dates, and usage metrics, helping organizations stay organized and avoid unnecessary costs." },
  { q: "How do I optimize my SaaS stack?", a: "SaaSGama analyzes software usage, identifies redundant applications, highlights underutilized licenses, and helps businesses build a more efficient SaaS ecosystem." },
  { q: "What is the best SaaS spend management solution?", a: "SaaSGama provides SaaS spend management tools that help organizations monitor software expenses, optimize budgets, and maximize the value of their software investments." },
  { q: "How can IT teams manage software subscriptions efficiently?", a: "SaaSGama gives IT teams a centralized platform to track subscriptions, manage licenses, monitor usage, automate renewals, and maintain software visibility." },
  { q: "Which SaaS management platform is best for growing businesses?", a: "SaaSGama is designed for growing businesses that need better control over software subscriptions, spending, license management, and operational efficiency." },
  { q: "How do I track software usage across my organization?", a: "SaaSGama provides software usage insights and analytics, helping businesses understand adoption rates, identify inactive licenses, and improve resource allocation." },
  { q: "What are the benefits of SaaS license optimization?", a: "SaaSGama helps businesses reduce waste, improve license utilization, lower software costs, enhance visibility, and ensure employees have access to the tools they need." },
  { q: "How does SaaSGama help businesses manage software subscriptions?", a: "SaaSGama centralizes SaaS subscription management by tracking licenses, monitoring spending, managing renewals, analyzing usage, and providing actionable insights to optimize software operations." },
];

const featureCards = [
  {
    bg: "from-blue-50 to-cyan-50", border: "border-blue-100", iconBg: "from-blue-500 to-blue-600",
    icon: <TrendingUp className="w-8 h-8 text-white" />,
    title: "Smart Cost Optimization",
    desc: "AI-powered recommendations identify unused licenses and optimization opportunities automatically",
    stat: "Average savings: 35-45%", statColor: "text-blue-600",
  },
  {
    bg: "from-green-50 to-emerald-50", border: "border-green-100", iconBg: "from-green-500 to-green-600",
    icon: <Shield className="w-8 h-8 text-white" />,
    title: "Automated Renewal Tracking",
    desc: "Never miss a renewal again with smart alerts and automated contract management",
    stat: "100% renewal visibility", statColor: "text-green-600",
  },
  {
    bg: "from-purple-50 to-violet-50", border: "border-purple-100", iconBg: "from-purple-500 to-purple-600",
    icon: <Users className="w-8 h-8 text-white" />,
    title: "Team License Management",
    desc: "Effortlessly assign, track, and optimize licenses across your entire organization",
    stat: "Unlimited team members", statColor: "text-purple-600",
  },
  {
    bg: "from-orange-50 to-red-50", border: "border-orange-100", iconBg: "from-orange-500 to-red-500",
    icon: <BarChart3 className="w-8 h-8 text-white" />,
    title: "Usage Analytics",
    desc: "Deep insights into software usage patterns and ROI optimization opportunities",
    stat: "Real-time monitoring", statColor: "text-orange-600",
  },
  {
    bg: "from-indigo-50 to-blue-50", border: "border-indigo-100", iconBg: "from-indigo-500 to-blue-500",
    icon: <Zap className="w-8 h-8 text-white" />,
    title: "5-Minute Setup",
    desc: "Connect your existing tools instantly with our one-click integrations",
    stat: "50+ integrations", statColor: "text-indigo-600",
  },
  {
    bg: "from-teal-50 to-cyan-50", border: "border-teal-100", iconBg: "from-teal-500 to-cyan-500",
    icon: <Target className="w-8 h-8 text-white" />,
    title: "Compliance Management",
    desc: "Stay compliant with automated audit trails and license compliance monitoring",
    stat: "Enterprise-grade security", statColor: "text-teal-600",
  },
];

export default function FeaturesPage() {
  const [activeCard, setActiveCard] = useState(0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>SaaS Management Features for Spend & License Tracking</title>
        <meta name="description" content="Explore SaaSGama's SaaS Management Features with SaaS Spend Management, license tracking, and cost optimization tools to control software expenses efficiently." />
        <meta name="keywords" content="SaaS Management Features, SaaS Spend Management, License Tracking, SaaS Cost Optimization" />
        <link rel="canonical" href="https://saasgama.com/features" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/features" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="SaaS Management Features for Spend & License Tracking" />
        <meta property="og:description" content="Explore SaaSGama's SaaS Management Features with SaaS Spend Management, license tracking, and cost optimization tools to control software expenses efficiently." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SaaS Management Features for Spend & License Tracking" />
        <meta name="twitter:description" content="Explore SaaSGama's SaaS Management Features with SaaS Spend Management, license tracking, and cost optimization tools to control software expenses efficiently." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "SoftwareApplication", "@id": "https://saasgama.com/features#software", "name": "SaaSGama", "applicationCategory": "BusinessApplication", "applicationSubCategory": "SaaS Management Platform", "operatingSystem": "Web", "url": "https://saasgama.com/features", "description": "SaaSGama is a SaaS management platform that helps organizations track software subscriptions, optimize licenses, control SaaS spending, monitor renewals, and improve software visibility across teams.", "image": "https://saasgama.com/logo.png", "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }, "featureList": ["SaaS Subscription Management", "License Tracking", "Renewal Monitoring", "Software Usage Analytics", "Spend Optimization", "Contract Management", "Vendor Management", "Centralized Dashboard", "Shadow IT Detection", "Cost Control"] },
            { "@type": "Service", "@id": "https://saasgama.com/features#service", "name": "SaaS Management Services", "provider": { "@type": "Organization", "name": "SaaSGama" }, "serviceType": "SaaS Subscription Management", "description": "Manage SaaS applications, licenses, renewals, contracts, spending, and software usage from a centralized platform." },
            { "@type": "Organization", "@id": "https://saasgama.com/#organization", "name": "SaaSGama", "url": "https://saasgama.com", "logo": "https://saasgama.com/logo.png", "description": "SaaSGama helps businesses gain visibility and control over SaaS subscriptions, software licenses, renewals, and spending." },
            { "@type": "FAQPage", "@id": "https://saasgama.com/features#faq", "mainEntity": featuresFaqs.map(faq => ({ "@type": "Question", "name": faq.q, "acceptedAnswer": { "@type": "Answer", "text": faq.a } })) },
            { "@type": "WebPage", "@id": "https://saasgama.com/features#webpage", "url": "https://saasgama.com/features", "name": "SaaSGama Features", "description": "Discover SaaSGama features for SaaS subscription management, license optimization, software spend control, renewal tracking, and usage analytics.", "about": { "@id": "https://saasgama.com/features#software" }, "keywords": ["SaaS Management Platform", "SaaS Subscription Management", "License Management Software", "SaaS Spend Management", "Software Asset Management", "Subscription Tracking Software", "Renewal Management Platform", "SaaS Cost Optimization", "Software Usage Analytics", "Vendor Management Software"] }
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader showBackButton />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6">
              SaaS Management{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Features
              </span>
            </h1>
            <p className="text-base md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              Everything you need to manage, optimize, and control your SaaS portfolio with intelligent automation and actionable insights.
            </p>
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Complete SaaS Management Suite
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From discovery to optimization, SAASGAMA provides all the tools you need to take control of your software spending.
            </p>
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <div
              className={`bg-gradient-to-br ${featureCards[activeCard].bg} p-6 rounded-2xl border ${featureCards[activeCard].border} shadow-lg`}
              onTouchStart={(e) => { (e.currentTarget as any)._touchX = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                const dx = e.changedTouches[0].clientX - (e.currentTarget as any)._touchX;
                if (dx < -50) setActiveCard((activeCard + 1) % featureCards.length);
                else if (dx > 50) setActiveCard((activeCard - 1 + featureCards.length) % featureCards.length);
              }}
            >
              <div className={`w-14 h-14 bg-gradient-to-r ${featureCards[activeCard].iconBg} rounded-2xl flex items-center justify-center mb-4`}>
                {featureCards[activeCard].icon}
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-800">{featureCards[activeCard].title}</h3>
              <p className="text-gray-600 text-sm mb-3">{featureCards[activeCard].desc}</p>
              <div className={`text-sm font-semibold ${featureCards[activeCard].statColor}`}>{featureCards[activeCard].stat}</div>
            </div>

            {/* Dot indicators */}
            <div className="flex justify-center gap-2 mt-4">
              {featureCards.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveCard(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === activeCard ? "bg-blue-600 w-4" : "bg-gray-300"}`}
                />
              ))}
            </div>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {featureCards.map((card, i) => (
              <div key={i} className={`group bg-gradient-to-br ${card.bg} p-8 rounded-2xl border ${card.border} hover:shadow-xl transition-all hover:-translate-y-1`}>
                <div className={`w-16 h-16 bg-gradient-to-r ${card.iconBg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  {card.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">{card.title}</h3>
                <p className="text-gray-600 mb-4">{card.desc}</p>
                <div className={`text-sm font-semibold ${card.statColor}`}>{card.stat}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Deep Dive into Features</h2>
              <p className="text-base md:text-xl text-gray-600">
                Explore the comprehensive capabilities that make SAASGAMA the leading SaaS management platform
              </p>
            </div>

            <div className="space-y-16">
              {/* Cost Optimization Detail */}
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-xl md:text-3xl font-bold mb-6 text-gray-800">AI-Powered Cost Optimization</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    Our intelligent algorithms continuously analyze your software usage patterns to identify cost-saving opportunities you might miss manually.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Automatically detect unused and underutilized licenses</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Smart downsizing recommendations based on actual usage</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Bulk license optimization across all applications</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">ROI analysis for every software investment</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl">
                  <div className="text-center">
                    <div className="text-2xl md:text-4xl font-bold text-blue-600 mb-2">$127K</div>
                    <div className="text-gray-600 mb-4">Average annual savings</div>
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      <div className="bg-white p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">43%</div>
                        <div className="text-sm text-gray-600">Cost Reduction</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">87</div>
                        <div className="text-sm text-gray-600">Licenses Optimized</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytics Detail */}
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-2xl order-2 lg:order-1">
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Usage Trends</span>
                        <span className="text-sm font-semibold text-green-600">↗ 23%</span>
                      </div>
                      <div className="h-8 bg-gradient-to-r from-orange-200 to-red-200 rounded"></div>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">98.5%</div>
                        <div className="text-sm text-gray-600">License Utilization</div>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">Real-time</div>
                        <div className="text-sm text-gray-600">Data Updates</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="order-1 lg:order-2">
                  <h3 className="text-xl md:text-3xl font-bold mb-6 text-gray-800">Advanced Usage Analytics</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    Get complete visibility into how your team uses software with detailed analytics and actionable insights.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Real-time usage monitoring and reporting</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">User activity tracking and productivity metrics</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Custom dashboards and automated reports</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Predictive analytics for future planning</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Security & Compliance Detail */}
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-xl md:text-3xl font-bold mb-6 text-gray-800">Enterprise Security & Compliance</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    Built with security-first principles and compliance requirements in mind, ensuring your data stays protected.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">SOC 2 Type II certified infrastructure</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">End-to-end encryption for all data</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Complete audit trails and activity logs</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">GDPR and CCPA compliance support</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-8 rounded-2xl">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Shield className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-lg font-bold text-gray-800">SOC 2</div>
                      <div className="text-sm text-gray-600">Certified</div>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Target className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-lg font-bold text-gray-800">99.9%</div>
                      <div className="text-sm text-gray-600">Uptime SLA</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Frequently Asked Questions</h2>
              <p className="text-base md:text-xl text-gray-600">
                Common questions about SaaSGama's SaaS management features
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {featuresFaqs.map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border border-gray-200 rounded-lg px-6 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-800 hover:text-blue-600">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pt-2">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-5xl font-bold mb-6">
              Ready to experience these features?
            </h2>
            <p className="text-base md:text-xl mb-8 text-blue-100">
              See how SAASGAMA can transform your SaaS management and optimize your costs
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg"
                asChild
              >
                <Link href="/register">
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 text-lg font-semibold rounded-xl transition-all bg-white"
                asChild
              >
                <Link href="/pricing">View Pricing</Link>
              </Button>
            </div>

          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}


